import { getSettings } from '../services/settings'
import type { Character } from '../data/cutscenes'
import {
  AdaptiveLipSyncTiming,
  LIP_SYNC_SAMPLE_HZ,
  mouthCueAtWithLead,
  type LipSyncTimingController,
  type LipSyncTrack,
  type MouthShape,
} from './CutsceneLipSync'

export interface VoiceProfile {
  rate: number
  pitch: number
  volume: number
  voiceHints: string[]
}

export const VOICE_PROFILES: Record<Character, VoiceProfile> = {
  bluebob: { rate: 1.06, pitch: 1.28, volume: 0.95, voiceHints: ['Samantha', 'Ava', 'Victoria'] },
  krabs: { rate: 0.84, pitch: 0.66, volume: 1, voiceHints: ['Fred', 'Daniel', 'Ralph'] },
  king: { rate: 0.72, pitch: 0.48, volume: 1, voiceHints: ['Alex', 'Daniel', 'Arthur'] },
  zap: { rate: 1.18, pitch: 1.5, volume: 0.9, voiceHints: ['Victoria', 'Samantha', 'Ava'] },
}

export type CutsceneVoiceState = 'idle' | 'speaking' | 'muted' | 'unavailable'
export type CutsceneVoiceSource = 'none' | 'recorded' | 'system'

export interface VoiceUtterance {
  readonly text: string
  lang: string
  pitch: number
  rate: number
  volume: number
  voice: SpeechSynthesisVoice | null
  onstart: (() => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
}

export interface VoiceSynth {
  cancel(): void
  getVoices(): SpeechSynthesisVoice[]
  speak(utterance: VoiceUtterance): void
}

type UtteranceFactory = (text: string) => VoiceUtterance

export interface VoiceAudio {
  currentTime: number
  duration?: number
  preload: string
  volume: number
  onplay: ((event: Event) => unknown) | null
  onended: ((event: Event) => unknown) | null
  onerror: ((event: Event) => unknown) | null
  play(): Promise<void>
  pause(): void
}

type AudioFactory = (url: string) => VoiceAudio | null

function browserSynth(): VoiceSynth | null {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null
  return {
    cancel: () => window.speechSynthesis.cancel(),
    getVoices: () => window.speechSynthesis.getVoices(),
    speak: (utterance) => window.speechSynthesis.speak(utterance as SpeechSynthesisUtterance),
  }
}

function browserUtterance(text: string): VoiceUtterance {
  return new SpeechSynthesisUtterance(text) as unknown as VoiceUtterance
}

function browserAudio(url: string): VoiceAudio | null {
  if (typeof Audio === 'undefined') return null
  return new Audio(url)
}

export class CutsceneVoice {
  private muted = false
  private state: CutsceneVoiceState = 'idle'
  private source: CutsceneVoiceSource = 'none'
  private speechId = 0
  private activeAudio: VoiceAudio | null = null
  private lipSyncTrack: LipSyncTrack | null = null
  private lipSyncTimer: number | null = null
  private sampledMouthCue: MouthShape | null = null
  private lastPlayhead = -1
  private lastProgressElapsed = 0
  private watchdogElapsed = 0
  private systemStartedElapsed = 0
  private recordedVolume = 1
  private recordedFallback: (() => void) | null = null

  constructor(
    private readonly synth: VoiceSynth | null = browserSynth(),
    private readonly createUtterance: UtteranceFactory = browserUtterance,
    private readonly createAudio: AudioFactory = browserAudio,
    private readonly lipSyncTiming: LipSyncTimingController = new AdaptiveLipSyncTiming(),
  ) {}

  get isMuted(): boolean {
    return this.muted
  }

  get isSpeaking(): boolean {
    return this.state === 'speaking'
  }

  get currentState(): CutsceneVoiceState {
    return this.muted ? 'muted' : this.state
  }

  get currentSource(): CutsceneVoiceSource {
    return this.source
  }

  get recordedPlayhead(): number | null {
    return this.source === 'recorded' && this.activeAudio ? this.activeAudio.currentTime : null
  }

  get recordedDuration(): number | null {
    const duration = this.activeAudio?.duration ?? this.lipSyncTrack?.duration
    return duration && Number.isFinite(duration) && duration > 0 ? duration : null
  }

  /** The playhead used for phoneme selection, including the measured output compensation. */
  get lipSyncPlayhead(): number | null {
    if (this.source !== 'recorded' || !this.activeAudio) return null
    const playhead = this.activeAudio.currentTime
    if (!Number.isFinite(playhead)) return null
    const compensated = Math.max(0, playhead + this.lipSyncTiming.visualLeadSeconds)
    const duration = this.lipSyncTrack?.duration
    return duration && Number.isFinite(duration) && duration > 0 ? Math.min(duration, compensated) : compensated
  }

  canAutoAdvance(elapsed: number, character: Character, text: string): boolean {
    this.watchdogElapsed = Math.max(this.watchdogElapsed, Number.isFinite(elapsed) ? elapsed : 0)
    if (!this.isSpeaking) return true
    if (this.currentSource === 'recorded') {
      const playhead = this.activeAudio?.currentTime ?? 0
      if (Number.isFinite(playhead) && playhead > this.lastPlayhead) {
        this.lastPlayhead = playhead
        this.lastProgressElapsed = this.watchdogElapsed
      }
      if (this.watchdogElapsed - this.lastProgressElapsed > 15) {
        if (this.lastPlayhead <= 0 && this.recordedFallback) {
          this.recordedFallback()
          return !this.isSpeaking
        }
        this.stop()
        this.state = 'unavailable'
        return true
      }
      return false
    }
    if (this.watchdogElapsed - this.systemStartedElapsed > this.maxLineSeconds(character, text)) {
      this.stop()
      this.state = 'unavailable'
      return true
    }
    return false
  }

  get currentMouthCue(): MouthShape | null {
    if (this.source !== 'recorded' || !this.activeAudio || !this.lipSyncTrack) return null
    const currentTime = this.activeAudio.currentTime
    // Read the media clock on the render frame as well. The high-rate sampler
    // covers dropped frames, while this read guarantees that a visible cue
    // never trails a boundary by one timer tick.
    this.sampledMouthCue = mouthCueAtWithLead(this.lipSyncTrack, currentTime, this.lipSyncTiming.visualLeadSeconds)
    return this.sampledMouthCue
  }

  sampleFrameDuration(seconds: number): void {
    this.lipSyncTiming.sampleFrameDuration(seconds)
  }

  setMuted(muted: boolean): void {
    if (this.muted === muted) return
    this.muted = muted
    if (this.activeAudio) {
      this.activeAudio.volume = muted ? 0 : this.recordedVolume * getSettings().master
    } else if (muted && this.source === 'system') {
      this.stop()
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted)
    return this.muted
  }

  speak(character: Character, text: string, clipUrl?: string, lipSyncTrack?: LipSyncTrack): CutsceneVoiceState {
    this.stop()
    if (!text.trim()) return (this.state = 'unavailable')

    const speechId = this.speechId
    if (clipUrl) {
      const audio = this.createAudio(clipUrl)
      if (audio) return this.speakRecorded(audio, character, text, speechId, lipSyncTrack ?? null)
    }
    return this.speakSystem(character, text, speechId)
  }

  stop(): void {
    this.speechId++
    this.lastPlayhead = -1
    this.lastProgressElapsed = 0
    this.watchdogElapsed = 0
    this.systemStartedElapsed = 0
    this.recordedFallback = null
    this.stopLipSyncSampler()
    const audio = this.activeAudio
    this.activeAudio = null
    this.lipSyncTrack = null
    if (audio) {
      audio.onplay = null
      audio.onended = null
      audio.onerror = null
      audio.pause()
      try {
        audio.currentTime = 0
      } catch {
        // Some browsers reject seeking before metadata is available; pause is sufficient.
      }
    }
    this.synth?.cancel()
    this.source = 'none'
    this.state = this.muted ? 'muted' : 'idle'
  }

  maxLineSeconds(character: Character, text: string): number {
    const words = Math.max(1, text.trim().split(/\s+/).length)
    return Math.max(4, words / (2.35 * VOICE_PROFILES[character].rate) + 2.5)
  }

  private stopLipSyncSampler(): void {
    if (this.lipSyncTimer !== null) {
      window.clearTimeout(this.lipSyncTimer)
      this.lipSyncTimer = null
    }
    this.sampledMouthCue = null
  }

  private startLipSyncSampler(audio: VoiceAudio, track: LipSyncTrack | null, speechId: number): void {
    this.stopLipSyncSampler()
    if (!track) return

    const sample = () => {
      if (speechId !== this.speechId || this.activeAudio !== audio || this.lipSyncTrack !== track) {
        this.lipSyncTimer = null
        return
      }
      this.sampledMouthCue = mouthCueAtWithLead(track, audio.currentTime, this.lipSyncTiming.visualLeadSeconds)
      this.lipSyncTimer = window.setTimeout(sample, 1000 / LIP_SYNC_SAMPLE_HZ)
    }

    // Unit tests and non-browser consumers still get the first media-clock
    // sample without creating a timer that could keep the process alive.
    this.sampledMouthCue = mouthCueAtWithLead(track, audio.currentTime, this.lipSyncTiming.visualLeadSeconds)
    if (typeof window !== 'undefined') sample()
  }

  private speakRecorded(
    audio: VoiceAudio,
    character: Character,
    text: string,
    speechId: number,
    lipSyncTrack: LipSyncTrack | null,
  ): CutsceneVoiceState {
    const profile = VOICE_PROFILES[character]
    // Probe once when a recorded performance starts. The bounded result compensates for the
    // device output buffer while keeping the media clock as the single source of truth.
    this.lipSyncTiming.probePlatformLatency()
    this.activeAudio = audio
    this.lipSyncTrack = lipSyncTrack
    this.startLipSyncSampler(audio, lipSyncTrack, speechId)
    audio.preload = 'auto'
    this.recordedVolume = profile.volume
    audio.volume = this.muted ? 0 : this.recordedVolume * getSettings().master

    const settle = () => {
      if (speechId !== this.speechId || this.activeAudio !== audio) return
      this.stopLipSyncSampler()
      this.activeAudio = null
      this.lipSyncTrack = null
      this.recordedFallback = null
      audio.onplay = null
      audio.onended = null
      audio.onerror = null
      this.source = 'none'
      this.state = 'idle'
    }
    const fallback = () => {
      if (speechId !== this.speechId || this.activeAudio !== audio) return
      this.stopLipSyncSampler()
      this.activeAudio = null
      this.lipSyncTrack = null
      audio.onplay = null
      audio.onended = null
      audio.onerror = null
      audio.pause()
      this.recordedFallback = null
      if (audio.currentTime > 0 || this.lastPlayhead > 0) {
        this.source = 'none'
        this.state = 'unavailable'
      } else this.speakSystem(character, text, speechId)
    }
    this.recordedFallback = fallback
    audio.onplay = () => {
      if (speechId === this.speechId && this.activeAudio === audio) {
        this.source = 'recorded'
        this.state = 'speaking'
      }
    }
    audio.onended = settle
    audio.onerror = fallback
    this.source = 'recorded'
    this.state = 'speaking'
    try {
      audio.play().catch(fallback)
    } catch {
      fallback()
    }
    return this.state
  }

  private speakSystem(character: Character, text: string, speechId: number): CutsceneVoiceState {
    this.stopLipSyncSampler()
    this.lipSyncTrack = null
    this.systemStartedElapsed = this.watchdogElapsed
    if (this.muted) {
      this.source = 'none'
      return (this.state = 'muted')
    }
    if (!this.synth) {
      this.source = 'none'
      return (this.state = 'unavailable')
    }

    const profile = VOICE_PROFILES[character]
    const utterance = this.createUtterance(text)
    utterance.lang = 'en-US'
    utterance.rate = profile.rate
    utterance.pitch = profile.pitch
    utterance.volume = profile.volume * getSettings().master
    utterance.voice = this.pickVoice(profile)
    utterance.onstart = () => {
      if (speechId === this.speechId) {
        this.source = 'system'
        this.state = 'speaking'
      }
    }
    const settle = () => {
      if (speechId === this.speechId) {
        this.source = 'none'
        this.state = 'idle'
      }
    }
    utterance.onend = settle
    utterance.onerror = settle
    this.source = 'system'
    this.state = 'speaking'
    try {
      this.synth.speak(utterance)
    } catch {
      this.source = 'none'
      this.state = 'unavailable'
    }
    return this.state
  }

  private pickVoice(profile: VoiceProfile): SpeechSynthesisVoice | null {
    const voices = this.synth?.getVoices() ?? []
    for (const hint of profile.voiceHints) {
      const match = voices.find((voice) => voice.name.toLowerCase().includes(hint.toLowerCase()))
      if (match) return match
    }
    return voices.find((voice) => voice.lang.toLowerCase().startsWith('en')) ?? null
  }
}
